/**
 * BUYER-INFRA-R1 — Cabinet «Покупець» surface: retail default + FOP/LEGAL profiles.
 * Non-intrusive: retail shoppers never need this for checkout.
 */
import { useCallback, useEffect, useState } from "react";
import {
  buyerStatusRail,
  buyerTypeLabelUk,
  createBuyerProfileV2,
  fetchBuyerProfileChecklistV2,
  fetchBuyerProfilesV2,
  fetchBuyerVerificationPolicyV2,
  listBuyerDocumentsV2,
  patchBuyerProfileV2,
  submitBuyerProfileV2,
  uploadBuyerDocumentV2,
  type BuyerChecklistItem,
  type BuyerProfilePublic,
  type BuyerProfileType,
} from "./buyer-profiles-v2.js";

type Lang = "ua" | "en";

export function BuyerProfilesCabinetV2({
  lang = "ua",
  focusType = "all",
}: {
  lang?: Lang;
  /** CUSTOMER-R5 — Profile tab filter: FOP or company only. */
  focusType?: "all" | "FOP" | "LEGAL_ENTITY";
}) {
  const [profiles, setProfiles] = useState<BuyerProfilePublic[]>([]);
  const [retailLabel, setRetailLabel] = useState("Особисто · Роздрібний покупець");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState<BuyerProfileType | null>(null);
  const [legalName, setLegalName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [legalAddress, setLegalAddress] = useState("");
  const [iban, setIban] = useState("");
  const [bankName, setBankName] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [vatPayer, setVatPayer] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentVersion, setConsentVersion] = useState("");
  const [consentText, setConsentText] = useState("");
  const [checklistById, setChecklistById] = useState<Record<string, BuyerChecklistItem[]>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [docsById, setDocsById] = useState<Record<string, number>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    void fetchBuyerProfilesV2().then(async (res) => {
      if (!res.ok) {
        setError(res.message ?? (lang === "en" ? "Failed to load buyer profiles." : "Не вдалося завантажити."));
        setProfiles([]);
        setLoading(false);
        return;
      }
      setRetailLabel(res.retail_default.label_uk);
      setProfiles(res.profiles);
      const counts: Record<string, number> = {};
      await Promise.all(
        res.profiles.map(async (p) => {
          const docs = await listBuyerDocumentsV2(p.id);
          if (docs.ok) counts[p.id] = docs.documents.length;
        }),
      );
      setDocsById(counts);
      setLoading(false);
    });
  }, [lang]);

  useEffect(() => {
    load();
    void fetchBuyerVerificationPolicyV2().then((p) => {
      if (p.ok) {
        setConsentVersion(p.consent_version ?? "");
        setConsentText(p.consent_text_uk ?? "");
      }
    });
  }, [load]);

  const loadChecklist = async (profileId: string) => {
    const res = await fetchBuyerProfileChecklistV2(profileId);
    if (res.ok) setChecklistById((prev) => ({ ...prev, [profileId]: res.documents }));
  };

  const createProfile = async () => {
    if (!formOpen) return;
    setBusyId("create");
    setError(null);
    setOkMsg(null);
    const res = await createBuyerProfileV2({
      type: formOpen,
      legal_name: legalName.trim(),
      tax_id: taxId.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      legal_address: legalAddress.trim() || null,
      contact_name: signatoryName.trim() || null,
    });
    setBusyId(null);
    if (!res.ok) {
      setError(res.message ?? (lang === "en" ? "Could not create profile." : "Не вдалося створити."));
      return;
    }
    if (res.profile) {
      await patchBuyerProfileV2(res.profile.id, {
        profile_details: {
          iban: iban.trim() || null,
          bank_name: bankName.trim() || null,
          signatory_name: signatoryName.trim() || null,
          vat_payer: vatPayer,
        },
      });
    }
    setFormOpen(null);
    setLegalName("");
    setTaxId("");
    setPhone("");
    setEmail("");
    setLegalAddress("");
    setIban("");
    setBankName("");
    setSignatoryName("");
    setVatPayer(false);
    setOkMsg(
      res.reused
        ? lang === "en"
          ? "Existing profile reused."
          : "Існуючий профіль використано повторно."
        : lang === "en"
          ? "Profile created as draft."
          : "Профіль створено як чернетку.",
    );
    if (res.profile) setExpandedId(res.profile.id);
    load();
  };

  const onUpload = async (profileId: string, file: File | null, documentType: string) => {
    if (!file) return;
    setBusyId(`up:${profileId}`);
    setError(null);
    const res = await uploadBuyerDocumentV2({ profileId, file, documentType });
    setBusyId(null);
    if (!res.ok) {
      setError(res.message ?? (lang === "en" ? "Upload failed." : "Завантаження не вдалося."));
      return;
    }
    setOkMsg(lang === "en" ? "Document uploaded." : "Документ завантажено.");
    await loadChecklist(profileId);
    load();
  };

  const onSubmit = async (profileId: string) => {
    if (!consentAccepted) {
      setError(
        lang === "en"
          ? "Confirm business verification consent before submitting."
          : "Підтвердіть згоду на перевірку перед поданням.",
      );
      return;
    }
    setBusyId(`sub:${profileId}`);
    setError(null);
    const res = await submitBuyerProfileV2(profileId, {
      verification_consent: true,
      verification_consent_version: consentVersion,
    });
    setBusyId(null);
    if (!res.ok) {
      setError(res.message ?? (lang === "en" ? "Submit failed." : "Не вдалося надіслати."));
      return;
    }
    setOkMsg(
      res.application_number
        ? lang === "en"
          ? `Sent for verification · ${res.application_number}`
          : `Надіслано на перевірку · ${res.application_number}`
        : lang === "en"
          ? "Sent for verification."
          : "Документи передано на перевірку.",
    );
    load();
  };

  return (
    <div data-testid="sv2-cabinet-buyer-panel" data-focus-type={focusType}>
      <p className="sv2-muted" style={{ marginBottom: 10, fontSize: 13 }}>
        {focusType === "FOP"
          ? lang === "en"
            ? "Add FOP details and documents when buying for business."
            : "Додайте реквізити та документи, якщо купуєте для бізнесу."
          : focusType === "LEGAL_ENTITY"
            ? lang === "en"
              ? "Company / LLC details and verification documents."
              : "Реквізити компанії / ТОВ та документи для перевірки."
            : lang === "en"
              ? "Buyer is who pays / needs documents. Delivery recipient stays separate."
              : "Покупець — хто оплачує / потребує документів. Одержувач доставки окремо."}
      </p>

      {focusType === "all" ? (
      <div
        className="sv2-info-card sv2-cabinet-card"
        data-testid="sv2-buyer-retail-default"
        data-rail="completed"
      >
        <div className="sv2-order-list-row">
          <strong>{lang === "en" ? "Personally · Retail buyer" : retailLabel}</strong>
        </div>
        <div className="sv2-muted" style={{ fontSize: 13 }}>
          {lang === "en"
            ? "Default for the shop. No tax ID or verification required."
            : "Звичайний стан магазину. ЄДРПОУ / РНОКПП і перевірка не потрібні."}
        </div>
      </div>
      ) : null}

      {loading ? (
        <p className="sv2-muted" style={{ fontSize: 13 }} data-testid="sv2-buyer-loading">
          {lang === "en" ? "Loading…" : "Завантаження…"}
        </p>
      ) : null}
      {error ? (
        <div className="sv2-alert sv2-alert-err" style={{ marginTop: 10 }} data-testid="sv2-buyer-error">
          {error}
          <button type="button" className="sv2-cta sv2-secondary sv2-cta-compact" style={{ marginTop: 8 }} onClick={load}>
            {lang === "en" ? "Retry" : "Спробувати ще"}
          </button>
        </div>
      ) : null}
      {okMsg ? (
        <div className="sv2-alert sv2-alert-ok" style={{ marginTop: 10 }} data-testid="sv2-buyer-ok">
          {okMsg}
        </div>
      ) : null}

      {focusType === "all" ? (
      <h4 style={{ margin: "16px 0 8px", fontSize: 15 }} data-testid="sv2-buyer-business-heading">
        {lang === "en" ? "Business profiles" : "Бізнес-профілі"}
      </h4>
      ) : null}

      {profiles.filter((p) => focusType === "all" || p.type === focusType).length === 0 && !loading ? (
        <div className="sv2-info-card sv2-cabinet-card" data-testid="sv2-buyer-empty">
          {focusType === "FOP"
            ? lang === "en"
              ? "No FOP profiles yet."
              : "Ще немає профілів ФОП."
            : focusType === "LEGAL_ENTITY"
              ? lang === "en"
                ? "No company profiles yet."
                : "Ще немає профілів компанії."
              : lang === "en"
                ? "No FOP or company profiles yet. Retail checkout works without them."
                : "Ще немає профілів ФОП чи компанії. Роздрібне оформлення працює без них."}
        </div>
      ) : (
        profiles
          .filter((p) => focusType === "all" || p.type === focusType)
          .map((p) => {
          const expanded = expandedId === p.id;
          const canSubmit =
            p.verification_status === "DRAFT" ||
            p.verification_status === "NEEDS_CHANGES" ||
            p.verification_status === "REVERIFY_REQUIRED";
          return (
            <div
              key={p.id}
              className="sv2-info-card sv2-cabinet-card sv2-delivery-profile-card"
              data-testid="sv2-buyer-profile-card"
              data-rail={buyerStatusRail(p.verification_status)}
              data-verification-status={p.verification_status}
            >
              <div className="sv2-order-list-row">
                <strong>
                  {buyerTypeLabelUk(p.type)} · {p.legal_name || p.display_name}
                </strong>
                <span className="sv2-pill" data-testid="sv2-buyer-status-pill">
                  {p.verification_status_uk}
                </span>
              </div>
              {p.tax_id_masked ? (
                <div className="sv2-muted" style={{ fontSize: 13 }}>
                  {p.tax_id_type === "EDRPOU" ? "ЄДРПОУ" : "РНОКПП"} {p.tax_id_masked}
                </div>
              ) : null}
              <div className="sv2-muted" style={{ fontSize: 12, marginTop: 4 }}>
                {lang === "en" ? "Documents" : "Документи"}: {docsById[p.id] ?? 0}
                {p.selectable_as_verified_buyer
                  ? lang === "en"
                    ? " · selectable at checkout"
                    : " · можна обрати в оформленні"
                  : ""}
              </div>
              {p.review_note ? (
                <div className="sv2-alert sv2-alert-warn" style={{ marginTop: 8, fontSize: 12 }}>
                  {p.review_note}
                </div>
              ) : null}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                <button
                  type="button"
                  className="sv2-cta sv2-secondary sv2-cta-compact"
                  data-testid="sv2-buyer-expand"
                  onClick={() => {
                    const next = expanded ? null : p.id;
                    setExpandedId(next);
                    if (next) void loadChecklist(next);
                  }}
                >
                  {expanded
                    ? lang === "en"
                      ? "Hide"
                      : "Сховати"
                    : lang === "en"
                      ? "Documents"
                      : "Документи"}
                </button>
                {canSubmit ? (
                  <button
                    type="button"
                    className="sv2-cta sv2-cta-compact"
                    data-testid="sv2-buyer-submit"
                    disabled={busyId === `sub:${p.id}`}
                    onClick={() => void onSubmit(p.id)}
                  >
                    {lang === "en" ? "Send for verification" : "Надіслати на перевірку"}
                  </button>
                ) : null}
              </div>
              {expanded ? (
                <div style={{ marginTop: 12 }} data-testid="sv2-buyer-docs-panel">
                  <h5 style={{ margin: "0 0 8px", fontSize: 14 }}>
                    {lang === "en" ? "Documents for verification" : "Документи для перевірки"}
                  </h5>
                  {(checklistById[p.id] ?? []).map((item) => (
                    <div key={item.document_type} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {item.label_uk}{" "}
                        <span className="sv2-muted" style={{ fontWeight: 400 }}>
                          {item.requirement === "REQUIRED"
                            ? lang === "en"
                              ? "(required)"
                              : "(обовʼязково)"
                            : item.requirement === "CONDITIONAL"
                              ? lang === "en"
                                ? "(if applicable)"
                                : "(за потреби)"
                              : lang === "en"
                                ? "(optional)"
                                : "(необовʼязково)"}
                        </span>
                      </div>
                      <div className="sv2-muted" style={{ fontSize: 12 }}>
                        {item.explanation_uk}
                      </div>
                      <div className="sv2-muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {item.satisfied
                          ? lang === "en"
                            ? `Uploaded (${item.uploaded_count})`
                            : `Завантажено (${item.uploaded_count})`
                          : lang === "en"
                            ? "Not uploaded"
                            : "Не завантажено"}
                      </div>
                      {canSubmit ? (
                        <label className="sv2-address-field" style={{ display: "block", marginTop: 6 }}>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                            capture="environment"
                            className="sv2-input sv2-input-visible"
                            data-testid={`sv2-buyer-doc-input-${item.document_type}`}
                            disabled={busyId === `up:${p.id}`}
                            onChange={(e) => {
                              const f = e.target.files?.[0] ?? null;
                              void onUpload(p.id, f, item.document_type);
                              e.target.value = "";
                            }}
                          />
                        </label>
                      ) : null}
                    </div>
                  ))}
                  {canSubmit ? (
                    <label className="sv2-address-field" style={{ display: "flex", gap: 8, marginTop: 12 }}>
                      <input
                        type="checkbox"
                        checked={consentAccepted}
                        data-testid="sv2-buyer-verification-consent"
                        onChange={(e) => setConsentAccepted(e.target.checked)}
                      />
                      <span style={{ fontSize: 12 }}>
                        {consentText ||
                          (lang === "en"
                            ? "I agree to business verification processing."
                            : "Погоджуюсь на обробку даних для перевірки контрагента.")}
                      </span>
                    </label>
                  ) : null}
                  {p.application_number ? (
                    <p className="sv2-muted" style={{ fontSize: 12, marginTop: 8 }}>
                      {lang === "en" ? "Application" : "Заявка"}: {p.application_number}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
        {focusType === "all" || focusType === "FOP" ? (
        <button
          type="button"
          className="sv2-cta sv2-secondary"
          data-testid="sv2-buyer-add-fop"
          onClick={() => {
            setFormOpen("FOP");
            setOkMsg(null);
          }}
        >
          {lang === "en" ? "Add FOP" : "Додати ФОП"}
        </button>
        ) : null}
        {focusType === "all" || focusType === "LEGAL_ENTITY" ? (
        <button
          type="button"
          className="sv2-cta sv2-secondary"
          data-testid="sv2-buyer-add-company"
          onClick={() => {
            setFormOpen("LEGAL_ENTITY");
            setOkMsg(null);
          }}
        >
          {lang === "en" ? "Add company" : "Додати компанію"}
        </button>
        ) : null}
      </div>

      {formOpen ? (
        <div className="sv2-info-card sv2-cabinet-card" style={{ marginTop: 12 }} data-testid="sv2-buyer-create-form">
          <strong>
            {formOpen === "FOP"
              ? lang === "en"
                ? "New FOP profile"
                : "Новий профіль ФОП"
              : lang === "en"
                ? "New company profile"
                : "Новий профіль компанії"}
          </strong>
          <label className="sv2-address-field" style={{ display: "block", marginTop: 10 }}>
            <span className="sv2-address-label">{lang === "en" ? "Legal name" : "Юридична назва"}</span>
            <input
              className="sv2-input sv2-input-visible"
              value={legalName}
              data-testid="sv2-buyer-legal-name"
              onChange={(e) => setLegalName(e.target.value)}
            />
          </label>
          <label className="sv2-address-field" style={{ display: "block", marginTop: 10 }}>
            <span className="sv2-address-label">
              {formOpen === "LEGAL_ENTITY" ? "ЄДРПОУ" : "РНОКПП"}
            </span>
            <input
              className="sv2-input sv2-input-visible"
              value={taxId}
              data-testid="sv2-buyer-tax-id"
              inputMode="numeric"
              onChange={(e) =>
                setTaxId(e.target.value.replace(/\D/g, "").slice(0, formOpen === "LEGAL_ENTITY" ? 8 : 10))
              }
            />
          </label>
          <label className="sv2-address-field" style={{ display: "block", marginTop: 10 }}>
            <span className="sv2-address-label">
              {formOpen === "FOP"
                ? lang === "en"
                  ? "Registration address"
                  : "Адреса реєстрації"
                : lang === "en"
                  ? "Legal address"
                  : "Юридична адреса"}
            </span>
            <input
              className="sv2-input sv2-input-visible"
              value={legalAddress}
              data-testid="sv2-buyer-legal-address"
              onChange={(e) => setLegalAddress(e.target.value)}
            />
          </label>
          <label className="sv2-address-field" style={{ display: "block", marginTop: 10 }}>
            <span className="sv2-address-label">IBAN</span>
            <input
              className="sv2-input sv2-input-visible"
              value={iban}
              data-testid="sv2-buyer-iban"
              onChange={(e) => setIban(e.target.value.toUpperCase())}
            />
          </label>
          <label className="sv2-address-field" style={{ display: "block", marginTop: 10 }}>
            <span className="sv2-address-label">{lang === "en" ? "Bank" : "Банк"}</span>
            <input
              className="sv2-input sv2-input-visible"
              value={bankName}
              data-testid="sv2-buyer-bank"
              onChange={(e) => setBankName(e.target.value)}
            />
          </label>
          <label className="sv2-address-field" style={{ display: "block", marginTop: 10 }}>
            <span className="sv2-address-label">
              {lang === "en" ? "Signatory / contact name" : "Підписант / контакт"}
            </span>
            <input
              className="sv2-input sv2-input-visible"
              value={signatoryName}
              data-testid="sv2-buyer-signatory"
              onChange={(e) => setSignatoryName(e.target.value)}
            />
          </label>
          <label className="sv2-address-field" style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input
              type="checkbox"
              checked={vatPayer}
              data-testid="sv2-buyer-vat-payer"
              onChange={(e) => setVatPayer(e.target.checked)}
            />
            <span className="sv2-address-label" style={{ margin: 0 }}>
              {lang === "en" ? "VAT payer" : "Платник ПДВ"}
            </span>
          </label>
          <label className="sv2-address-field" style={{ display: "block", marginTop: 10 }}>
            <span className="sv2-address-label">{lang === "en" ? "Phone (optional)" : "Телефон (необовʼязково)"}</span>
            <input
              className="sv2-input sv2-input-visible"
              value={phone}
              data-testid="sv2-buyer-phone"
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <label className="sv2-address-field" style={{ display: "block", marginTop: 10 }}>
            <span className="sv2-address-label">Email</span>
            <input
              type="email"
              className="sv2-input sv2-input-visible"
              value={email}
              data-testid="sv2-buyer-email"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              type="button"
              className="sv2-cta"
              data-testid="sv2-buyer-create-save"
              disabled={busyId === "create" || !legalName.trim() || !taxId.trim()}
              onClick={() => void createProfile()}
            >
              {lang === "en" ? "Save draft" : "Зберегти чернетку"}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-secondary"
              onClick={() => setFormOpen(null)}
            >
              {lang === "en" ? "Cancel" : "Скасувати"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
